import { Outlet } from 'react-router-dom';
import Logo from '@/assets/logo-image/Logo.svg';
import Image from './base-ui/image';
import LetsStayTogetherWhite from '@/assets/logo-image/lets-stay-together-white.png';
import { useLayoutVisibility } from '@/utils/common-utils';
import { useState, type ReactNode } from 'react';
import { AuthProvider } from '@/contexts/auth-context';
import IntercomWrapper from './intercom-wrapper';
import { Menu } from 'lucide-react';
import { ComponentModalFullScreen } from './common/modal';
import Button from './base-ui/button';
import { signOut } from '@/utils/auth-utils';

const Layout = ({ children }: { children?: ReactNode }) => {
  const [showModal, setShowModal] = useState(false);
  const { showFooter, showHeader, hasIntercom, showFooterLifeX, showMenu } = useLayoutVisibility();
  return (
    <div id="lifex-app-container" className="flex flex-col overflow-hidden" style={{ height: 'var(--app-height)' }}>
      {/* Header */}
      {showHeader && (
        <header className="shrink-0 bg-transparent text-white p-1 pr-3 pl-3 mb-1 mt-1 flex items-center justify-between">
          <div className="relative w-16 md:w-22 lg:w-26">
            <Image src={Logo} alt="Logo" style={{ objectFit: 'contain' }} />
          </div>
          <div>
            {showMenu && (
              <div onClick={() => setShowModal(true)} className="cursor-pointer">
                <Menu size={32} />
              </div>
            )}
          </div>
        </header>
      )}

      {/* Main (scrollable) */}
      <div className={`flex-1 overflow-y-auto flex flex-col mt-1.5`} style={{ height: '100%' }}>
        <ComponentModalFullScreen isOpen={showModal} showClose={true} onClose={() => setShowModal(false)}>
          <div className="flex flex-col items-center justify-center gap-4 mt-4 p-4">
            <div
              className="font-gothia flex flex-col justify-center items-center m-2 text-5xl text-[var(--lifexlight)] gap-8"
              style={{ height: 'calc(100vh - 342px)' }}
            >
              <span>Help</span>
              <span>Community</span>
              <span>Contact</span>
            </div>
            <div className="w-32">
              <Button
                onClick={() => {
                  signOut();
                }}
                variant="primary"
              >
                <div className="flex gap-2 justify-center">Logout</div>
              </Button>
            </div>
            <div className="flex gap-1.5 mt-12">
              <a href="https://yourlifex.com/privacy/" className="text-gray-600 underline px-0">
                Privacy
              </a>
              <span className="text-gray-600">&</span>
              <a href="https://yourlifex.com/terms/" className="text-gray-600 underline px-0">
                Terms
              </a>
            </div>
          </div>
        </ComponentModalFullScreen>
        <main className={`flex-1`}>
          {children ?? (
            <AuthProvider>
              <IntercomWrapper>
                <Outlet />
              </IntercomWrapper>
            </AuthProvider>
          )}
        </main>
        {showFooter && (
          <footer
            className={`shrink-0 bg-transparent flex items-center ${
              showFooterLifeX ? 'justify-between' : 'justify-end'
            } h-16 sm:h-16 md:h-18 lg:h-20 px-2`}
          >
            {showFooterLifeX && (
              <div className="relative w-18 sm:w-18 md:w-22 lg:w-26">
                <Image src={Logo} alt="Logo" style={{ objectFit: 'contain' }} />
              </div>
            )}
            <div className={`w-58 sm:w-58 md:w-66 lg:w-74 xl:w-82 h-full ${hasIntercom ? 'mr-16' : 'mr-0'}`}>
              <Image src={LetsStayTogetherWhite} alt="Let's Stay Together" style={{ objectFit: 'contain' }} />
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default Layout;
